import { useTranslation } from 'react-i18next';
import { Square, Play } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Button } from '@/shared/components/ui/button';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useMyPendingTasks } from '@/features/tasks/public';
import { TimerDisplay } from './TimerDisplay';
import { useActiveTimer } from '../hooks/useActiveTimer';
import { useStartTimer, useStopTimer } from '../hooks/useTimeTrackingMutations';

export function GlobalTimerPopover() {
  const { t } = useTranslation('timeTracking');
  const { data: activeTimer, isLoading, isError, refetch } = useActiveTimer();
  const stopTimer = useStopTimer();
  const startTimer = useStartTimer();
  const { tasks: allPendingTasks, isLoading: isPendingLoading } = useMyPendingTasks({
    PageSize: 5,
  });

  const hasActiveTimer = !!activeTimer?.id;
  const taskId = activeTimer?.task?.id ?? null;
  const pendingTasks = allPendingTasks.filter((task) => task.id !== taskId);

  function handleStop() {
    if (!taskId) return;
    stopTimer.mutate(taskId);
  }

  return (
    <div className="w-72 p-3" data-testid="time-tracking-global-popover">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        {t('popover.title')}
      </p>

      {/* Active task section */}
      <div>
        <p className="text-xs font-medium mb-2">{t('popover.active')}</p>

        {isLoading && (
          <div className="flex items-center justify-between gap-2" aria-hidden="true">
            <div className="flex flex-col gap-1 flex-1">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-6 w-12" />
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2">
            <p className="text-xs text-destructive flex-1">{t('timer.error')}</p>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => refetch()}>
              {t('timer.retry')}
            </Button>
          </div>
        )}

        {!isLoading && !isError && !hasActiveTimer && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="time-tracking-popover-no-active"
          >
            {t('timer.noActive')}
          </p>
        )}

        {!isLoading && !isError && hasActiveTimer && (
          <div
            className="flex items-center justify-between gap-2"
            data-testid="time-tracking-popover-active-item"
          >
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{activeTimer.task.title ?? '—'}</p>
              <p className="text-xs text-muted-foreground truncate">
                {activeTimer.task.project?.name ?? '—'}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <TimerDisplay
                startTime={activeTimer.startTime}
                className="text-xs font-medium tabular-nums text-green-600"
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label={t('timer.stop')}
                    data-testid="time-tracking-global-stop-btn"
                    disabled={stopTimer.isPending}
                    onClick={handleStop}
                  >
                    {stopTimer.isPending ? (
                      <span
                        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                        aria-hidden="true"
                      />
                    ) : (
                      <Square className="h-3.5 w-3.5 fill-current" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('timer.stop')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      <Separator className="my-3" />

      {/* Pending tasks section */}
      <div>
        <p className="text-xs font-medium mb-2">{t('popover.pending')}</p>

        {isPendingLoading && (
          <div className="flex flex-col gap-1.5" aria-hidden="true">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        )}

        {!isPendingLoading && pendingTasks.length === 0 && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="time-tracking-popover-no-pending"
          >
            {t('popover.pendingEmpty')}
          </p>
        )}

        {!isPendingLoading && pendingTasks.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {pendingTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between gap-2"
                data-testid="time-tracking-popover-pending-item"
              >
                <p className="text-xs truncate flex-1">{task.title ?? '—'}</p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      aria-label={t('timer.start')}
                      disabled={startTimer.isPending}
                      onClick={() => startTimer.mutate(task.id)}
                      data-testid={`time-tracking-popover-start-${task.id}`}
                    >
                      {startTimer.isPending ? (
                        <span
                          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                          aria-hidden="true"
                        />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('timer.start')}</TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
